import { useEffect, useState } from 'react';
import { Card, Col, Flex, Row } from 'antd';
import CustomTitle from '../../../components/CustomTitle';
import CustomParagraph from '../../../components/CustomParagraph';
import LocalShipping from '../../../assets/images/20road.png';
import AirplaneFlying from '../../../assets/images/20air.png';
import { ShipmentLeg } from '@/apis/types';

interface Location {
  name: string;
}

interface ShipmentData {
  reference_number: string;
  from_location?: Location;
  to_location: Location;
}

interface CardData {
  id: string;
  legs_info?: {
    legs?: ShipmentLeg[];
  };
}

interface ShipmentPreviewCardProps {
  card: CardData;
  toggleDrawer: (action: string, type: string, id: string) => void;
  shipment: ShipmentData;
}

/**
 * Preview card component for displaying shipment information in bulk operations
 *
 * @param card - Card data containing shipment legs and journey information
 * @param toggleDrawer - Callback to open shipment detail drawer
 * @param shipment - Shipment data with reference number and locations
 */
function ShipmentPreviewCard({
  card,
  toggleDrawer,
  shipment,
}: ShipmentPreviewCardProps) {
  const [legs, setLegs] = useState<ShipmentLeg[]>([]);

  useEffect(() => {
    if (card?.legs_info?.legs?.length) {
      setLegs(card.legs_info.legs);
    } else {
      setLegs([]);
    }
  }, [card]);

  return (
    <Card
      style={{
        borderRadius: '12px',
        border: '2px solid #EEE',
        cursor: 'pointer',
        height: 'fit-content',
      }}
      bodyStyle={{ padding: '15px' }}
      onClick={() => toggleDrawer('open', 'Detail', card?.id)}
    >
      <Row gutter={[24, 16]} justify='space-between'>
        <Col>
          <>
            <div
              style={{
                maxWidth: '200px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              <CustomTitle
                text={shipment.reference_number}
                fontSize='12px'
                fontWeight={600}
                lineHeight='20px'
                color='var(--text-primary)'
                marginBottom='4px'
              />
              <CustomTitle
                text={'--'}
                fontSize='12px'
                fontWeight={600}
                marginTop={10}
                lineHeight='20px'
                color='var(--text-primary)'
                marginBottom='4px'
              />
            </div>
          </>
        </Col>

        <Col>
          <CustomParagraph
            text={'--'}
            fontSize='12px'
            fontWeight={500}
            color='var(--text-primary)'
            marginBottom='0'
          />

          <CustomParagraph
            text={shipment?.from_location?.name}
            fontSize='12px'
            fontWeight={400}
            color='var(--text-light)'
            marginBottom='0'
          />
          <CustomParagraph
            text={'--'}
            fontSize='12px'
            fontWeight={400}
            color='var(--text-light)'
            marginBottom='4px'
          />
        </Col>

        <Col>
          <Flex align='flex-end' vertical style={{ textAlign: 'right' }}>
            <CustomParagraph
              text={'--'}
              fontSize='12px'
              fontWeight={500}
              color='var(--text-primary)'
              marginBottom='0'
            />
            <CustomParagraph
              text={shipment.to_location.name}
              fontSize='12px'
              fontWeight={400}
              color='var(--text-light)'
              marginBottom='0'
            />
            <CustomParagraph
              text={'--'}
              fontSize='12px'
              fontWeight={400}
              color='var(--text-light)'
              marginBottom='4px'
            />
          </Flex>
        </Col>
      </Row>

      <Row className='shipment-progress' style={{ marginTop: '12px' }}>
        <Col span={24}>
          <Flex align='center'>
            <ul className='custom-steps-display'>
              {legs.length > 0 ? (
                legs.map((leg: ShipmentLeg, index: number) => (
                  <li
                    key={index}
                    className={leg.status === 'travelled' ? 'active' : ''}
                    style={{
                      width: `${leg.distribution}%`,
                      marginRight: '4px',
                    }}
                  >
                    {leg.status === 'travelled' && (
                      <div
                        className='centerImage'
                        style={{ backgroundColor: 'var(--surface-container)' }}
                      >
                        <img
                          src={
                            leg.mode === 'truck'
                              ? LocalShipping
                              : AirplaneFlying
                          }
                          alt={leg.mode}
                          style={{ width: '20px' }}
                        />
                      </div>
                    )}
                  </li>
                ))
              ) : (
                <li style={{ width: '100%' }} />
              )}
            </ul>
          </Flex>
        </Col>
      </Row>
    </Card>
  );
}

export default ShipmentPreviewCard;
